/**
 * La Coochie FAQ (sections/lc-faq.liquid): opens and scrolls to a deep-linked question (#anchor), and switches
 * category panels in the "tabs" layout. Accordion behaviour itself is native <details name="…">.
 */
import { Component } from '@theme/component';

class LcFaq extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#openHash();
    window.addEventListener('hashchange', this.#openHash);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.#openHash);
  }

  /** @param {Event} event */
  selectTab(event) {
    const tab = event.target instanceof Element ? event.target.closest('[data-target]') : null;
    if (!(tab instanceof HTMLElement)) return;
    this.#activate(tab.dataset.target ?? '');
  }

  /** Phone (tabs layout, strip hidden): the category title toggles its questions; one category open at a time. @param {Event} event */
  toggleCategory(event) {
    const category = event.target instanceof Element ? event.target.closest('.lc-faq__category') : null;
    if (!(category instanceof HTMLElement)) return;
    this.#openCategory(category.hasAttribute('data-open') ? null : category);
  }

  /** @param {HTMLElement | null} category */
  #openCategory(category) {
    for (const panel of this.querySelectorAll('.lc-faq__category')) {
      const open = panel === category;
      panel.toggleAttribute('data-open', open);
      panel.querySelector('.lc-faq__category-toggle')?.setAttribute('aria-expanded', String(open));
    }
  }

  /** @param {string} handle */
  #activate(handle) {
    for (const tab of this.querySelectorAll('.lc-faq__tab')) {
      tab.setAttribute('aria-selected', String(/** @type {HTMLElement} */ (tab).dataset.target === handle));
    }
    for (const panel of this.querySelectorAll('.lc-faq__category')) {
      panel.toggleAttribute('data-inactive', /** @type {HTMLElement} */ (panel).dataset.category !== handle);
    }
  }

  #openHash = () => {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) return;
    const target = this.querySelector(`#${CSS.escape(id)}`);
    if (!target) return;
    const category = target.closest('.lc-faq__category');
    if (category instanceof HTMLElement && this.dataset.layout === 'tabs') {
      this.#activate(category.dataset.category ?? '');
      this.#openCategory(category);
    }
    if (target instanceof HTMLDetailsElement) target.open = true;
    // instant, not the page's smooth scroll-behavior: after a load with a #fragment WebKit leaves smooth scrolls of
    // .page-wrapper stuck at 0 (desktop Safari never moved; instant scrolls land) — 2026-10-11
    const scroll = () => target.scrollIntoView({ block: 'start', behavior: 'instant' });
    requestAnimationFrame(scroll);
    // On ≥990 Horizon makes .page-wrapper the scroll container; when this runs before that CSS applies (Safari, tabs layout)
    // the first scroll hits the window and is a no-op — scroll again once the page has fully loaded (2026-09-30).
    if (document.readyState !== 'complete') window.addEventListener('load', () => requestAnimationFrame(scroll), { once: true });
    // the heading face is font-display: block — when it lands after the scroll the target shifts by the swap; scroll once more
    document.fonts?.ready.then(() => requestAnimationFrame(scroll));
  };
}

if (!customElements.get('lc-faq')) {
  customElements.define('lc-faq', LcFaq);
}
